<template>
    <section v-if="visible" class="flex flex-col gap-2" :aria-busy="loading">
        <div class="flex items-baseline gap-2 flex-wrap">
            <h3 class="scaffold-details-small text-ink-muted">{{ t("reflectionsAdmin.individual360s.title") }}</h3>
            <span class="details text-ink-muted">{{ t("reflectionsAdmin.individual360s.subtitle") }}</span>
        </div>

        <BaseCard padding="md" class="flex flex-col gap-4">
            <!-- Mounted once and left in place so each new result count is announced. -->
            <p class="sr-only" role="status">{{ resultAnnouncement }}</p>
            <div class="flex items-center gap-2 flex-wrap">
                <div class="flex items-center gap-2" role="group" :aria-label="t('reflectionsAdmin.individual360s.selfReflectionGroup')">
                    <BaseButton
                        v-for="option in stateOptions"
                        :key="option.value"
                        theme="neutral"
                        size="sm"
                        class="rounded-full scaffold-body-small"
                        :class="{ 'bg-toggle-option-selected': state === option.value }"
                        :aria-pressed="state === option.value"
                        @click="selectState(option.value)">
                        {{ option.label }}
                    </BaseButton>
                </div>
                <div class="ml-auto flex items-center gap-2 flex-wrap">
                    <BaseSelect
                        :model-value="templateId"
                        :options="templateOptions"
                        :aria-label="t('reflectionsAdmin.individual360s.template.label')"
                        trigger-class="min-w-44"
                        @update:model-value="selectTemplate" />
                    <BaseSelect
                        :model-value="status"
                        :options="statusOptions"
                        :aria-label="t('reflectionsAdmin.individual360s.status.label')"
                        trigger-class="min-w-44"
                        @update:model-value="selectStatus" />
                    <BaseSelect
                        :model-value="report"
                        :options="reportOptions"
                        :aria-label="t('reflectionsAdmin.individual360s.report.label')"
                        trigger-class="min-w-44"
                        @update:model-value="selectReport" />
                    <div class="w-full md:w-auto md:min-w-72">
                        <BaseInput v-model="query" type="search" :maxlength="SEARCH_MAX_LENGTH" :placeholder="searchLabel" :aria-label="searchLabel">
                            <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                        </BaseInput>
                    </div>
                    <BaseButton theme="neutral" size="sm" :disabled="exporting || !page || page.total === 0" @click="onExportCsv">
                        <i class="bi bi-filetype-csv" aria-hidden="true"></i>
                        {{ exporting ? t("reflectionsAdmin.individual360s.exporting") : t("reflectionsAdmin.individual360s.exportCsv") }}
                    </BaseButton>
                    <BaseButton
                        v-if="templateId !== ALL_TEMPLATES"
                        theme="primary"
                        size="sm"
                        :disabled="!reportAvailable"
                        :aria-describedby="reportHint ? REPORT_HINT_ID : undefined"
                        @click="onViewReport">
                        <i class="bi bi-bar-chart" aria-hidden="true"></i>
                        {{ t("reflectionsAdmin.individual360s.viewReport") }}
                    </BaseButton>
                </div>
                <div aria-live="polite">
                    <p v-if="reportHint" :id="REPORT_HINT_ID" class="details text-ink-muted" data-testid="report-hint">
                        {{ reportHint }}
                        <BaseButton v-if="reportTotalFailed" theme="ghost" size="sm" @click="loadReportTotal">{{ t("common.tryAgain") }}</BaseButton>
                    </p>
                </div>
            </div>

            <p v-if="loadError" class="body-small text-status-error" role="alert">{{ loadError }}</p>
            <p v-else-if="loading && !page" class="body-small text-ink-muted">{{ t("reflectionsAdmin.individual360s.loading") }}</p>
            <p v-else-if="page && page.total === 0" class="body-small text-ink-muted">{{ emptyText }}</p>

            <template v-else-if="page">
                <AdminParticipantTable
                    :rows="page.rows"
                    :hide-rater-status="hideRaterStatus"
                    :participant-header="t('reflectionsAdmin.individual360s.participantHeader')"
                    :class="{ 'opacity-60': loading }">
                    <template #subline="{ row }">
                        <span class="details text-ink-muted truncate">{{ sublineFor(row) }}</span>
                    </template>
                </AdminParticipantTable>

                <div class="flex items-center gap-3 flex-wrap px-2 details text-ink-muted">
                    <span>{{ footerText }}</span>
                    <div class="ml-auto inline-flex items-center gap-1">
                        <BaseButton
                            theme="neutral"
                            size="sm"
                            icon-only
                            :aria-label="t('reflectionsAdmin.panel.previousPage')"
                            :disabled="page.page <= 1 || loading"
                            @click="goToPage(page.page - 1)">
                            <i class="bi bi-chevron-left" aria-hidden="true"></i>
                        </BaseButton>
                        <span class="min-w-18 text-center">{{ t("reflectionsAdmin.panel.pageOf", { page: page.page, pages: page.page_count }) }}</span>
                        <BaseButton
                            theme="neutral"
                            size="sm"
                            icon-only
                            :aria-label="t('reflectionsAdmin.panel.nextPage')"
                            :disabled="page.page >= page.page_count || loading"
                            @click="goToPage(page.page + 1)">
                            <i class="bi bi-chevron-right" aria-hidden="true"></i>
                        </BaseButton>
                    </div>
                </div>
            </template>

            <BaseNotification v-if="exportError" theme="error" :text="exportError" @dismiss="exportError = null" />
        </BaseCard>
    </section>
</template>

<script setup lang="ts">
import { refDebounced } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type {
    AdminIndividual360sPage,
    AdminParticipantRow,
    AdminReportFilter,
    IndividualCycleStatusFilter,
    IndividualSelfReflectionFilter,
    IndividualTemplateOption,
} from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import AdminParticipantTable from "~vue/pages/reflections/AdminParticipantTable.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { downloadApiFile } from "~vue/utils/downloadApiFile";
import { exportErrorCode, MIN_INDIVIDUAL_REPORT_PARTICIPANTS, TOO_FEW_PARTICIPANTS_CODE } from "~vue/utils/exportErrorCode";
import { logUserInteraction } from "~vue/utils/logUtils";
import type { AcceptableValue } from "reka-ui";
import { computed, onBeforeUnmount, onMounted, type Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    hideRaterStatus?: boolean;
    reportTemplateId?: string;
}

const LIST_PATH = "/v1/reflections/admin/individual-360s";
const SEARCH_DEBOUNCE_MS = 300;
// The API rejects longer terms with a 422; the input caps typing and the slice covers pasted or programmatic values.
const SEARCH_MAX_LENGTH = 100;
const REPORT_HINT_ID = "individual-360s-report-hint";
// The template select needs a real option for "no filter"; an empty string is reserved by the select primitive.
const ALL_TEMPLATES = "all";

const { hideRaterStatus = false, reportTemplateId } = defineProps<Props>();
const emit = defineEmits<{ (event: "view-report", templateId: string): void }>();
const { t } = useI18n();

const state = ref<IndividualSelfReflectionFilter>("all");
const status = ref<IndividualCycleStatusFilter>("all");
const report = ref<AdminReportFilter>("all");
const templateId = ref<string>(ALL_TEMPLATES);
const query = ref("");
const debouncedQuery = refDebounced(query, SEARCH_DEBOUNCE_MS);
const effectiveQuery = ref("");
const pageNumber = ref(1);
const page = ref<AdminIndividual360sPage | null>(null);
// How many people on the selected template have submitted, regardless of the
// search, status and report filters: that is the population the report covers.
const reportTotal = ref<number | null>(null);
const reportTotalFailed = ref(false);
const templates = ref<IndividualTemplateOption[]>([]);
const loading = ref(false);
const loadError = ref<string | null>(null);
const exporting = ref(false);
const exportError = ref<string | null>(null);

const stateOptions = computed<{ value: IndividualSelfReflectionFilter; label: string }[]>(() => [
    { value: "all", label: t("reflectionsAdmin.individual360s.filter.all") },
    { value: "in_progress", label: t("reflectionsAdmin.individual360s.filter.inProgress") },
    { value: "submitted", label: t("reflectionsAdmin.individual360s.filter.submitted") },
]);

// "Any status" leads so the default reads first, like the other selects.
const statusOptions = computed<{ key: IndividualCycleStatusFilter; label: string }[]>(() => [
    { key: "all", label: t("reflectionsAdmin.individual360s.status.all") },
    { key: "open", label: t("reflectionsAdmin.individual360s.status.open") },
    { key: "closed", label: t("reflectionsAdmin.individual360s.status.closed") },
]);

const reportOptions = computed<{ key: AdminReportFilter; label: string }[]>(() => [
    { key: "all", label: t("reflectionsAdmin.individual360s.report.all") },
    { key: "ready_unviewed", label: t("reflectionsAdmin.individual360s.report.readyUnviewed") },
    { key: "viewed", label: t("reflectionsAdmin.individual360s.report.viewed") },
    { key: "not_ready", label: t("reflectionsAdmin.individual360s.report.notReady") },
]);

const templateOptions = computed<{ key: string; label: string }[]>(() => [
    { key: ALL_TEMPLATES, label: t("reflectionsAdmin.individual360s.template.all") },
    ...templates.value.map((template) => ({ key: template.id, label: template.name })),
]);

const searchLabel = computed(() => t("reflectionsAdmin.individual360s.search"));

const isFiltered = computed(() => state.value !== "all" || status.value !== "all" || report.value !== "all" || templateId.value !== ALL_TEMPLATES || effectiveQuery.value !== "");

// The pooled report is per template and only covers submitted reflections, so
// both filters have to be in place before there is anything to open.
const reportAvailable = computed(
    () => templateId.value !== ALL_TEMPLATES && state.value === "submitted" && reportTotal.value !== null && reportTotal.value >= MIN_INDIVIDUAL_REPORT_PARTICIPANTS,
);

const reportHint = computed(() => {
    if (templateId.value === ALL_TEMPLATES || reportAvailable.value) {
        return "";
    }
    if (reportTotalFailed.value) {
        return t("reflectionsAdmin.individual360s.reportCountFailed");
    }
    if (reportTotal.value === null) {
        return "";
    }
    if (state.value !== "submitted") {
        return t("reflectionsAdmin.individual360s.reportNeedsSubmitted");
    }
    return reportTotal.value === 0
        ? t("reflectionsAdmin.individual360s.reportNoSubmitted")
        : t("reflectionsAdmin.individual360s.reportTooFewSubmitted", { count: MIN_INDIVIDUAL_REPORT_PARTICIPANTS });
});

// Nothing to manage and nothing being searched for: the hub reads better without the block.
const visible = computed(() => loadError.value !== null || page.value === null || page.value.organization_total > 0 || isFiltered.value);

const emptyText = computed(() => (isFiltered.value ? t("reflectionsAdmin.individual360s.noMatches") : t("reflectionsAdmin.individual360s.none")));

const footerText = computed(() => {
    if (!page.value) {
        return "";
    }
    const from = page.value.total ? (page.value.page - 1) * page.value.page_size + 1 : 0;
    const to = Math.min(page.value.total, page.value.page * page.value.page_size);
    return t("reflectionsAdmin.individual360s.showing", { from, to, total: page.value.total, submitted: page.value.submitted });
});

const resultAnnouncement = computed(() => {
    if (loading.value || !page.value) {
        return "";
    }
    return page.value.total === 0 ? emptyText.value : footerText.value;
});

// The list follows the debounced term; the export takes the term as typed, so a
// click right after typing downloads what the admin asked for.
function filterParams(searchTerm: string): URLSearchParams {
    const params = new URLSearchParams({
        state: state.value,
        report: report.value,
        status: status.value,
        q: searchTerm.slice(0, SEARCH_MAX_LENGTH),
    });
    if (templateId.value !== ALL_TEMPLATES) {
        params.set("template_id", templateId.value);
    }
    return params;
}

function sublineFor(row: AdminParticipantRow): string {
    return [row.template_name, row.job_title].filter(Boolean).join(" · ");
}

let requestSequence = 0;
let reportTotalSequence = 0;

async function loadReportTotal() {
    const sequence = ++reportTotalSequence;
    reportTotalFailed.value = false;
    if (templateId.value === ALL_TEMPLATES) {
        reportTotal.value = null;
        return;
    }
    const params = new URLSearchParams({ state: "submitted", report: "all", status: "all", template_id: templateId.value, page: "1" });
    const { data, error: fetchError } = await useFetch(`${LIST_PATH}?${params}`).json<AdminIndividual360sPage>();
    if (sequence !== reportTotalSequence) {
        return;
    }
    reportTotalFailed.value = Boolean(fetchError.value);
    reportTotal.value = fetchError.value ? null : (data.value?.total ?? null);
}

async function load() {
    const sequence = ++requestSequence;
    loading.value = true;
    loadError.value = null;
    const params = filterParams(effectiveQuery.value);
    params.set("page", String(pageNumber.value));
    const { data, error: fetchError } = await useFetch(`${LIST_PATH}?${params}`).json<AdminIndividual360sPage>();
    if (sequence !== requestSequence) {
        return;
    }
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || t("reflectionsAdmin.individual360s.loadFailed");
        return;
    }
    page.value = data.value ?? null;
    if (data.value) {
        templates.value = data.value.templates;
    }
}

function selectState(next: IndividualSelfReflectionFilter) {
    state.value = next;
    pageNumber.value = 1;
}

function selectStatus(next: AcceptableValue) {
    status.value = next as IndividualCycleStatusFilter;
    pageNumber.value = 1;
}

function selectTemplate(next: AcceptableValue) {
    templateId.value = String(next);
    pageNumber.value = 1;
}

function selectReport(next: AcceptableValue) {
    report.value = next as AdminReportFilter;
    pageNumber.value = 1;
}

function goToPage(next: number) {
    pageNumber.value = next;
}

async function onExportCsv() {
    await exportFile(exporting, AmplitudeEventNames.ASSESSMENT_INDIVIDUAL_360S_CSV_DOWNLOAD_CLICKED, "export-csv", "individual_360s.csv", query.value);
}

function onViewReport() {
    if (!reportAvailable.value) {
        return;
    }
    emit("view-report", templateId.value);
}

async function exportFile(busy: Ref<boolean>, event: AmplitudeEventNames, endpoint: string, fallbackFilename: string, searchTerm: string) {
    if (busy.value) {
        return;
    }
    void logUserInteraction(event, {});
    busy.value = true;
    exportError.value = null;
    try {
        const downloaded = await downloadApiFile(`${LIST_PATH}/${endpoint}?${filterParams(searchTerm)}`, fallbackFilename, async (response) => {
            exportError.value =
                (await exportErrorCode(response)) === TOO_FEW_PARTICIPANTS_CODE
                    ? t("reflectionsAdmin.individual360s.exportTooFewParticipants", { count: MIN_INDIVIDUAL_REPORT_PARTICIPANTS })
                    : t("reflectionsAdmin.individual360s.exportFailed");
        });
        if (!downloaded) {
            exportError.value ??= t("reflectionsAdmin.individual360s.exportFailed");
        }
    } catch {
        exportError.value = t("reflectionsAdmin.individual360s.exportFailed");
    } finally {
        busy.value = false;
    }
}

// A new search term always starts from page one.
watch(debouncedQuery, (value) => {
    if (effectiveQuery.value !== value) {
        effectiveQuery.value = value;
        pageNumber.value = 1;
    }
});

watch([state, status, report, templateId, effectiveQuery, pageNumber], () => {
    void load();
});

watch(templateId, () => {
    void loadReportTotal();
});

onMounted(() => {
    if (reportTemplateId) {
        // Back from the template report remounts the list; land on the
        // filters the report was opened from rather than the default list.
        state.value = "submitted";
        templateId.value = reportTemplateId;
    }
    void load();
    void loadReportTotal();
});

onBeforeUnmount(() => {
    requestSequence += 1;
    reportTotalSequence += 1;
});
</script>
